import { API, HoursSuggestion, MigrationPreview } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonVariant, Spinner } from "@swivl-mono/ui";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { DetailsNeedsInput, FlaggedGroup, flaggedGroups, hoursValuesToSuggest, overridesFor } from "./DetailsNeedsInput";
import { MigrationGroup } from "./MigrationGroup";
import { StepApplyBar, useStepApply } from "./StepApplyBar";
import { MigrationStepHandle, StepApplyOutcome } from "./workflow";

interface MigrateDetailsTabProps {
  orgId: number;
  /** Called after this step writes, so the workflow can refresh steps that read what it wrote. */
  onApplied: () => void;
}

/**
 * Step 1: review the legacy → v3 Details values that will migrate, then apply them from this step's
 * own `StepApplyBar`. Selection is held here and only the selected specs are written.
 */
/** Legacy hours values per suggestion request; each uncached one is an AI call. */
const SUGGESTION_CHUNK = 10;

export const MigrateDetailsTab = forwardRef<MigrationStepHandle, MigrateDetailsTabProps>(function MigrateDetailsTab({ orgId, onApplied }, ref) {
  const [preview, setPreview] = useState<MigrationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedSpecIds, setSelectedSpecIds] = useState<Set<string>>(new Set());
  const [expandedSpecIds, setExpandedSpecIds] = useState<Set<string>>(new Set());
  // Flagged rows fixed in place: group key -> the operator's value, written on Apply.
  const [resolved, setResolved] = useState<Map<string, string>>(new Map());
  const [suggestions, setSuggestions] = useState<Map<string, HoursSuggestion>>(new Map());
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const p = await API.Cortex.previewMigration(orgId);
      setPreview(p);
      // Default: only check specs that will actually write a value. Ones that already match, or
      // have no legacy value to migrate, start unchecked (the operator can still opt in).
      const toWrite = new Set<string>();
      for (const g of p.groups) for (const i of g.items) if (i.summary.wouldWriteCount > 0) toWrite.add(i.specId);
      setSelectedSpecIds(toWrite);
      setResolved(new Map());
      // Suggested hours for values the parser couldn't confirm, a few per request (each is an AI
      // call on a cache miss). Advisory: they only pre-fill the editor.
      const toSuggest = hoursValuesToSuggest(flaggedGroups(p.groups.flatMap((g) => g.items)));
      if (toSuggest.length > 0) {
        setSuggestionsLoading(true);
        void (async () => {
          for (let i = 0; i < toSuggest.length; i += SUGGESTION_CHUNK) {
            try {
              const batch = await API.Cortex.migrationHoursSuggestions(toSuggest.slice(i, i + SUGGESTION_CHUNK));
              setSuggestions((prev) => new Map([...prev, ...batch.map((s): [string, HoursSuggestion] => [s.value, s])]));
            } catch {
              // No suggestion for this chunk: those cards open with an empty editor.
            }
          }
        })().finally(() => setSuggestionsLoading(false));
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload().catch(() => {
      /* handled in reload */
    });
  }, [reload]);

  const allSpecIds = useMemo<string[]>(() => {
    if (!preview) return [];
    return preview.groups.flatMap((g) => g.items.map((i) => i.specId));
  }, [preview]);

  const totals = useMemo(() => {
    if (!preview) return { wouldWriteCount: 0, alreadyMatchCount: 0, activeQuarantineCount: 0, ticketedCount: 0, warningCount: 0 };
    let wouldWriteCount = 0;
    let alreadyMatchCount = 0;
    let activeQuarantineCount = 0; // quarantined and NOT yet tracked by a ticket
    let ticketedCount = 0; // quarantined but now tracked by an open review ticket
    let warningCount = 0;
    for (const g of preview.groups) {
      for (const i of g.items) {
        if (!selectedSpecIds.has(i.specId)) continue;
        wouldWriteCount += i.summary.wouldWriteCount;
        alreadyMatchCount += i.summary.alreadyMatchCount;
        warningCount += i.summary.warningCount;
        for (const r of i.rows) {
          if (!r.quarantined) continue;
          if (r.ticket) ticketedCount++;
          else activeQuarantineCount++;
        }
      }
    }
    return { wouldWriteCount, alreadyMatchCount, activeQuarantineCount, ticketedCount, warningCount };
  }, [preview, selectedSpecIds]);

  const handleSelectAll = useCallback(() => setSelectedSpecIds(new Set(allSpecIds)), [allSpecIds]);
  const handleSelectNone = useCallback(() => setSelectedSpecIds(new Set()), []);
  const handleToggleSelected = useCallback((specId: string) => {
    setSelectedSpecIds((prev) => {
      const next = new Set(prev);
      if (next.has(specId)) next.delete(specId);
      else next.add(specId);
      return next;
    });
  }, []);
  const handleToggleExpanded = useCallback((specId: string) => {
    setExpandedSpecIds((prev) => {
      const next = new Set(prev);
      if (next.has(specId)) next.delete(specId);
      else next.add(specId);
      return next;
    });
  }, []);

  const groups = useMemo<FlaggedGroup[]>(() => (preview ? flaggedGroups(preview.groups.flatMap((g) => g.items)) : []), [preview]);

  // Resolving a row also selects its field: a field whose rows were all held back starts unchecked.
  const handleResolve = useCallback((group: FlaggedGroup, value: string) => {
    setResolved((prev) => new Map(prev).set(group.key, value));
    setSelectedSpecIds((prev) => new Set(prev).add(group.item.specId));
  }, []);
  const handleUndo = useCallback((group: FlaggedGroup) => {
    setResolved((prev) => {
      const next = new Map(prev);
      next.delete(group.key);
      return next;
    });
  }, []);

  const handleApply = useCallback(async (): Promise<StepApplyOutcome> => {
    const res = await API.Cortex.applyMigration(orgId, [...selectedSpecIds], overridesFor(groups, resolved));
    onApplied();
    await reload();
    const parts = [`${res.written} value(s) written`];
    if (res.overridesApplied > 0) parts.push(`${res.overridesApplied} fixed by you`);
    if (res.quarantined > 0) parts.push(`${res.quarantined} flagged for review`);
    if (res.reviewTasksAdded > 0) parts.push(`${res.reviewTasksAdded} added to the review ticket`);
    return {
      message: parts.join(", "),
      ticketNumber: res.reviewTicketNumber,
      warning:
        res.overridesRejected > 0
          ? `${res.overridesRejected} of your value(s) weren't valid for their field, so those rows stayed flagged and went on the review ticket.`
          : undefined
    };
  }, [orgId, selectedSpecIds, groups, resolved, onApplied, reload]);

  useImperativeHandle(ref, () => ({ reload }), [reload]);
  const applyState = useStepApply(handleApply);

  if (loadError) {
    return <div className="body-sm p-6 text-red-500">Failed to load migration preview: {loadError}</div>;
  }
  if (loading || !preview) {
    return (
      <div className="body-sm flex items-center gap-2 p-6 text-grey-400">
        <Spinner /> Loading migration preview…
      </div>
    );
  }

  const totalSpecs = allSpecIds.length;
  const selectedCount = selectedSpecIds.size;

  // One consolidated "needs attention" strip instead of stacked banners; each entry shows only when non-zero.
  const statusItems: { dot: string; text: string; link?: string }[] = [];
  if (totals.activeQuarantineCount > 0)
    statusItems.push({
      dot: "bg-red-500",
      text: `${totals.activeQuarantineCount} value(s) can't migrate automatically — skipped for review (red below).`
    });
  if (totals.warningCount > 0)
    statusItems.push({ dot: "bg-yellow-500", text: `${totals.warningCount} value(s) will migrate on a guess — please verify (yellow below).` });
  if (totals.ticketedCount > 0)
    statusItems.push({ dot: "bg-grey-400", text: `${totals.ticketedCount} item(s) that can't migrate are tracked by open review tickets.` });
  if (preview.openTicketCount > 0)
    statusItems.push({
      dot: "bg-yellow-500",
      text: `${preview.openTicketCount} review ticket(s) from a previous migration still need attention before re-migrating.`,
      link: "/inbox"
    });

  return (
    <>
      <StepApplyBar
        description={`${totals.wouldWriteCount} value(s) will migrate from the ${selectedCount} selected variable(s).`}
        applyLabel="Apply Details"
        confirmText={`This writes the ${selectedCount} selected Details variable(s) for org ${orgId} and opens review tickets for anything flagged. Safe to re-run.`}
        disabled={selectedCount === 0}
        disabledReason="Select at least one variable to apply."
        state={applyState}
      />
      <div className="mx-auto max-w-5xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="heading-base">Details to migrate</h2>
            <p className="body-sm mt-1 text-grey-500">
              {selectedCount} of {totalSpecs} variables selected · would write {totals.wouldWriteCount} value(s) · {totals.alreadyMatchCount} already
              match.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant={ButtonVariant.GHOST} onClick={handleSelectAll}>
              Select all
            </Button>
            <Button variant={ButtonVariant.GHOST} onClick={handleSelectNone}>
              Select none
            </Button>
          </div>
        </div>

        {statusItems.length > 0 && (
          <div className="mb-6 flex flex-col gap-2 rounded border border-grey-200 bg-white p-3">
            {statusItems.map((s) => (
              <div key={s.text} className="body-sm flex items-center gap-2 text-grey-700">
                <span className={filterClasses("h-2 w-2 shrink-0 rounded-full", s.dot)} />
                <span>{s.text}</span>
                {s.link && (
                  <Link to={s.link} className="text-orange-600 hover:underline">
                    View tickets
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}

        <DetailsNeedsInput
          groups={groups}
          resolved={resolved}
          suggestions={suggestions}
          suggestionsLoading={suggestionsLoading}
          onResolve={handleResolve}
          onUndo={handleUndo}
        />

        {preview.groups.map((group) => (
          <MigrationGroup
            key={group.groupLabel}
            group={group}
            selectedSpecIds={selectedSpecIds}
            expandedSpecIds={expandedSpecIds}
            onToggleSelected={handleToggleSelected}
            onToggleExpanded={handleToggleExpanded}
          />
        ))}
      </div>
    </>
  );
});
